import * as Utils from './utils';

import { State } from './state.model';
import { Transition } from './transition.model';
import { BackendObjectRef } from './backend-integration.service';
import { Form } from './form.model';

export class Workflow implements BackendObjectRef {
  private static ID_PREFIX = 'w-';

  public readonly id: string;

  public name: string;

  /**
   * Roles used by the workflow
   *
   * Roles are unique and sorted alphabetically by role names.
   */
  public get roles(): BackendObjectRef[] {
    // Process transitions
    const result =  this.transitions
                        .reduce((resultTemp, transition) => {
                          transition.roles.forEach(r => resultTemp[r.id] = r);
                          return resultTemp;
                        }, {} as {[K: string]: BackendObjectRef});

    // Process initial transition
    if (this.initialTransition) {
      this.initialTransition.roles.forEach(r => result[r.id] = r);
    }

    // Sort result and return the Array
    return Object.values(result)
                 .sort((a, b) => a.name.localeCompare(b.name));
  }

  public states: State[];

  public transitions: Transition[];

  public form?: Form;

  public initialTransition?: Transition;

  public constructor(id: string|undefined,
                     name: string,
                     states?: State[],
                     transitions?: Transition[],
                     initialTransition?: Transition,
                     form?: Form) {
    this.id = id ?? `${Workflow.ID_PREFIX}${Utils.randomString()}`;
    this.name = name;
    this.states = states ?? [];
    this.transitions = transitions ?? [];
    this.initialTransition = initialTransition;
    this.form = form;
  }

  public copy(): Workflow {
    return new Workflow(this.id,
                        this.name,
                        [...this.states],
                        [...this.transitions],
                        this.initialTransition,
                        this.form);
  }

  //
  // States
  //

  public addState(state: State) {
    state.isDraft = false;
    this.states = [...this.states, state];
  }

  public updateState(state: State) {
    const idx = this.states.findIndex(s => s.id === state.id);
    const states = [...this.states];
    states[idx] = state;
    this.states = states;

    // Update transitions for the updated state
    const transitions = this.transitionsForState(state);
    transitions.forEach(transition => {
      const update = transition.copy();
      if (update.source.id === state.id) { update.source = state; }
      if (update.target.id === state.id) { update.target = state; }
      this.updateTransition(update);
    });
  }

  public stateByID(id: string): State | undefined {
    return this.states.find(o => o.id === id);
  }

  public statesForRole(roleID: string): State[] {
      const transitions = this.transitionsForRole(roleID);
      let result: State[] = transitions.reduce((states: State[], t: Transition) => {
          return [...states, t.source, t.target];
      }, []);
      result = result.filter((n, i) => result.indexOf(n) === i);
      return result;
  }

  //
  // Transitions
  //

  public addTransition(transition: Transition) {
    this.transitions = [...this.transitions, transition];
  }

  public deleteTransition(transitionID: string) {
    this.transitions = this.transitions.filter(t => t.id !== transitionID);
  }

  public updateTransition(transition: Transition) {
    if (transition.isInitialTransition) {
      this.initialTransition = transition;
      return;
    }

    const idx = this.transitions.findIndex(t => t.id === transition.id);
    const transitions = [...this.transitions];
    transitions[idx] = transition;
    this.transitions = transitions;
  }

  public transitionByID(transitionID: string): Transition | undefined {
    if (this.initialTransition && this.initialTransition.id === transitionID) {
      return this.initialTransition;
    }

    return this.transitions.find(o => o.id === transitionID);
  }

  public transitionsForState(state: State): Transition[] {
      return this.transitions.filter(t => {
        return t.source.id === state.id || t.target.id === state.id;
      });
  }

  public transitionsForRole(roleID: string): Transition[] {
      return this.transitions.filter(t => {
          return t.roles.findIndex(r => r.id === roleID);
      });
  }
}
